<script setup lang="ts">
import {computed, ref} from "vue";
import {ChevronDown, EyeOff, Plus} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Skeleton} from "@/core/components/ui/skeleton";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/core/components/ui/collapsible";
import CatalogIcon from "@/core/components/icon/CatalogIcon.vue";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import AppointmentTypeRow from "@/agenda/components/AppointmentTypeRow.vue";
import AppointmentTypeFormDialog from "@/agenda/components/AppointmentTypeFormDialog.vue";
import useAppointmentTypes from "@/agenda/composables/useAppointmentTypes";
import {appointmentTypeApi} from "@/container";
import {AppointmentType} from "@/core/interfaces/AppointmentType";
import {DEFAULT_APPOINTMENT_COLOR} from "@/agenda/constants";
import {
    AppointmentTypeStoreRequest,
    AppointmentTypeUpdateRequest
} from "@/core/http/resources/AppointmentTypeResource";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {useToastStore} from "@/stores/toast.store";

/**
 * A cache updater produced by a successful mutation: receives the current
 * list and returns the list to store, sparing a full refetch. Without one,
 * the list is refetched.
 */
type CacheUpdate = (list: AppointmentType[]) => AppointmentType[];

const {data: allTypes, isLoading, mutate, organizationId} = useAppointmentTypes({withHidden: true});

const types = computed(() => (allTypes.value ?? []).filter((type) => !type.is_hidden));
const hiddenTypes = computed(() => (allTypes.value ?? []).filter((type) => type.is_hidden));
const hiddenSectionOpen = ref(false);
const toastStore = useToastStore();

/**
 * Count of in-flight mutations: a boolean would be cleared by the first of
 * two overlapping mutations (e.g. a debounced duration PATCH landing while
 * a toggle is saving), re-enabling the controls too early.
 */
const pendingMutations = ref(0);
const saving = computed(() => pendingMutations.value > 0);

const formDialogOpen = ref(false);
const editedType = ref<AppointmentType | null>(null);
const deleteDialogOpen = ref(false);
const typeToDelete = ref<AppointmentType | null>(null);

/**
 * Run a mutation, then write its result into the SWR cache without refetching.
 * On failure, refetch so optimistic values (duration stepper) reset to the
 * server state. forceRevalidate bypasses swrv's deduping window, which would
 * otherwise silently drop back-to-back cache writes.
 */
const runMutation = async (mutation: () => Promise<CacheUpdate | undefined>) => {
    pendingMutations.value++;

    try {
        const applyToCache = await mutation();
        await mutate(applyToCache ? () => applyToCache(allTypes.value ?? []) : undefined, {forceRevalidate: true});

        return true;
    } catch (error) {
        await mutate(undefined, {forceRevalidate: true});

        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }

        return false;
    } finally {
        pendingMutations.value--;
    }
};

/**
 * Replace the updated type in the cached list.
 */
const replaceInList = (updated: AppointmentType): CacheUpdate => {
    return (list) => list.map((item) => item.id === updated.id ? updated : item);
};

const openCreateDialog = () => {
    editedType.value = null;
    formDialogOpen.value = true;
};

const openEditDialog = (type: AppointmentType) => {
    editedType.value = type;
    formDialogOpen.value = true;
};

const openDeleteDialog = (type: AppointmentType) => {
    typeToDelete.value = type;
    deleteDialogOpen.value = true;
};

/**
 * Keep only the fields that differ from the edited type, so submitting the
 * dialog cannot revert a field changed elsewhere in the meantime (e.g. a
 * debounced duration PATCH still pending when the dialog opened).
 */
const changedFields = (type: AppointmentType, request: AppointmentTypeStoreRequest): AppointmentTypeUpdateRequest => {
    const changes: AppointmentTypeUpdateRequest = {};

    if (!type.is_global && request.name !== type.name) {
        changes.name = request.name;
    }

    // Never sent on a template: the API refuses the key, null included.
    if (!type.is_global && request.default_description !== undefined
        && request.default_description !== (type.default_description ?? null)) {
        changes.default_description = request.default_description;
    }

    if (request.duration !== type.duration) {
        changes.duration = request.duration;
    }

    if ((request.color ?? '').toLowerCase() !== (type.color ?? '').toLowerCase()) {
        changes.color = request.color;
    }

    if (request.icon !== type.icon) {
        changes.icon = request.icon;
    }

    if (request.is_bookable_online !== type.is_bookable_online) {
        changes.is_bookable_online = request.is_bookable_online;
    }

    return changes;
};

/**
 * Create or update a type from the form dialog.
 */
const handleSubmit = async (request: AppointmentTypeStoreRequest) => {
    const edited = editedType.value;

    if (edited) {
        const changes = changedFields(edited, request);

        if (!Object.keys(changes).length) {
            formDialogOpen.value = false;

            return;
        }

        const success = await runMutation(async () => {
            const updated = await appointmentTypeApi().update(edited.id, changes);

            return replaceInList(updated);
        });

        if (success) {
            formDialogOpen.value = false;
        }

        return;
    }

    const currentOrganizationId = organizationId.value;

    if (!currentOrganizationId) {
        return;
    }

    const success = await runMutation(async () => {
        const created = await appointmentTypeApi().create(currentOrganizationId, request);

        return (list) => [...list, created];
    });

    if (success) {
        formDialogOpen.value = false;
    }
};

/**
 * Update the default duration of a type. The next appointment created with
 * this type will be pre-filled with the new duration.
 */
const handleDurationChange = (type: AppointmentType, duration: number) => {
    return runMutation(async () => {
        const updated = await appointmentTypeApi().update(type.id, {duration});

        return replaceInList(updated);
    });
};

const handleBookableToggle = (type: AppointmentType, isBookableOnline: boolean) => {
    return runMutation(async () => {
        const updated = await appointmentTypeApi().update(type.id, {is_bookable_online: isBookableOnline});

        return replaceInList(updated);
    });
};

/**
 * Soft delete an own type, or hide an Apodis template for the pharmacy: either
 * way it leaves the list but existing appointments keep it.
 *
 * The deleted type is captured before the alert dialog closes, because
 * AlertDialogAction closes the dialog before the click handler runs. It is
 * not reset afterwards: the dialog would still be fading out on it.
 */
const handleDelete = async () => {
    const type = typeToDelete.value;

    if (!type) {
        return;
    }

    await runMutation(async () => {
        await appointmentTypeApi().delete(type.id);

        return type.is_global
            ? replaceInList({...type, is_hidden: true})
            : (list) => list.filter((item) => item.id !== type.id);
    });
};

/**
 * Show a hidden template again. The list keeps hidden templates in their
 * place, so the API order survives without a refetch.
 */
const handleUnhide = (type: AppointmentType) => {
    return runMutation(async () => {
        const updated = await appointmentTypeApi().unhide(type.id);

        return replaceInList(updated);
    });
};
</script>

<template>
  <div class="p-6">
    <div class="mx-auto max-w-3xl rounded-lg bg-white shadow">
      <div class="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-4">
        <div>
          <h1>Types de rendez-vous</h1>
          <p class="text-sm text-gray-500">
            Personnalisez les types de rendez-vous proposés par votre pharmacie et leur durée par défaut.
          </p>
        </div>
        <Button class="shrink-0 bg-primary" @click="openCreateDialog">
          <Plus class="size-4" />
          Ajouter un type
        </Button>
      </div>
      <div v-if="isLoading || !organizationId" class="flex flex-col gap-3 p-4">
        <Skeleton v-for="index in 6" :key="index" class="h-12 w-full" />
      </div>
      <div v-else-if="types?.length" class="divide-y divide-gray-100">
        <AppointmentTypeRow
          v-for="type in types"
          :key="type.id"
          :type="type"
          :disabled="saving"
          @update-duration="handleDurationChange(type, $event)"
          @toggle-bookable="handleBookableToggle(type, $event)"
          @edit="openEditDialog(type)"
          @delete="openDeleteDialog(type)"
        />
      </div>
      <div v-else class="p-8 text-center text-sm text-gray-500">
        <p>Aucun type de rendez-vous.</p>
        <p>Ajoutez un type pour proposer des rendez-vous à vos patients.</p>
      </div>
    </div>
    <Collapsible
      v-if="hiddenTypes.length"
      v-model:open="hiddenSectionOpen"
      class="mx-auto mt-4 max-w-3xl rounded-lg bg-white shadow"
    >
      <CollapsibleTrigger
        class="flex w-full items-center gap-2 px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <EyeOff class="size-4 shrink-0 text-primary" />
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-semibold text-primary">Types masqués ({{ hiddenTypes.length }})</span>
          <span class="block truncate text-xs text-gray-500">
            Types du catalogue Apodis retirés de votre liste.
          </span>
        </span>
        <ChevronDown class="size-4 shrink-0 text-gray-400 transition-transform" :class="{'rotate-180': hiddenSectionOpen}" />
      </CollapsibleTrigger>
      <CollapsibleContent class="divide-y divide-gray-100 border-t border-slate-100">
        <div v-for="type in hiddenTypes" :key="type.id" class="flex items-center gap-4 px-4 py-3">
          <span
            class="size-3 shrink-0 rounded-full"
            :style="{backgroundColor: type.color ?? DEFAULT_APPOINTMENT_COLOR}"
          />
          <span class="flex size-[22px] shrink-0 text-gray-dark">
            <CatalogIcon :name="type.icon" class="size-full" />
          </span>
          <p class="flex-1 truncate font-medium">
            {{ type.name }}
          </p>
          <Button
            variant="outline"
            size="sm"
            :disabled="saving"
            @click="handleUnhide(type)"
          >
            Réafficher
          </Button>
        </div>
      </CollapsibleContent>
    </Collapsible>
    <AppointmentTypeFormDialog
      v-model:open="formDialogOpen"
      :type="editedType"
      :loading="saving"
      @submit="handleSubmit"
    />
    <AlertDialog v-model:open="deleteDialogOpen">
      <AlertDialogContent>
        <AlertDialogHeader v-if="typeToDelete?.is_global">
          <AlertDialogTitle>Retirer « {{ typeToDelete.name }} » de vos types ?</AlertDialogTitle>
          <AlertDialogDescription>
            Ce type ne sera plus proposé pour les nouveaux rendez-vous.
            Les rendez-vous existants de ce type sont conservés, et vous pourrez
            le réafficher à tout moment depuis les types masqués.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogHeader v-else>
          <AlertDialogTitle>Supprimer « {{ typeToDelete?.name }} » ?</AlertDialogTitle>
          <AlertDialogDescription>
            Ce type ne sera plus proposé pour les nouveaux rendez-vous.
            Les rendez-vous existants de ce type sont conservés.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction :disabled="saving" @click="handleDelete">
            {{ typeToDelete?.is_global ? 'Retirer' : 'Supprimer' }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
